import {
  DestroyRef,
  Directive,
  DoCheck,
  Injector,
  Input,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, NgModel } from '@angular/forms';
import { NonNullableFormBuilder } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

@Directive({
  selector: '[uiControlValueAccessor]',
  standalone: true,
})
export class ControlValueAccessorDirective<T> implements ControlValueAccessor, OnInit, DoCheck {
  readonly defaultValue = input<T>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: any) => void = () => {};

  private injector = inject(Injector);

  fb = inject(NonNullableFormBuilder);
  destroyRef = inject(DestroyRef);

  control!: FormControl<T>;
  disabled$ = new BehaviorSubject<boolean>(false);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set disabled(state: boolean) {
    this.setDisabledState(!!state);
  }

  ngOnInit(): void {
    this.setFormControl();
  }

  ngDoCheck(): void {
    this.setFormControl();
  }

  private setFormControl(): void {
    const ngControl = this.injector.get(NgControl, null); // Safe injection

    if (ngControl) {
      if (ngControl instanceof NgModel) {
        this.control = ngControl.control as FormControl<T>;
      } else {
        this.control = ngControl.control as FormControl<T>;
      }
    } else {
      this.control ??= this.fb.control(this.defaultValue() as T); // fallback if no form control provided
    }
  }

  writeValue(value: T): void {
    if (!this.control) {
      this.control = this.fb.control(value);
    } else if (this.control.value !== value) {
      this.control.setValue(value);
    }
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled$.next(isDisabled);
  }
}
